import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components/macro";
import { TableResponseData, ProductConfigContext, RequestStatusState } from "../../App";
import { t } from "../../utils/intl";
import { OfferComponent } from "../../components/OfferComponent";
import MainSelect from "../../components/UIComponents/Select";
import { Datepicker } from "../../components/UIComponents/DatePicker";
import { ProductForm } from "../../components/ProductForm";
import { ProductCategories } from "../../const/InitialCalcConfig";
import { useConfigList } from "../../helpers/customHooks/getConfigList";
import differenceInDays from "date-fns/differenceInDays";
import isFuture from "date-fns/isFuture";
import infoLogo from "../../assets/i-circle-icn.svg";
import isSameDay from "date-fns/isSameDay";
import { ProductConfig } from "../../types/requestTypes";
import GrayTextBox from "../../components/GrayTextBox";
import { Typo16Regular } from "../../components/UIComponents/Typography";
import { isValid } from "date-fns";
import dateFnsParse from "date-fns/parse";
import { mainDateValueFormat } from "../../utils/dateUtils";

const Home = () => {
  const [selectedProduct, setSelectedProduct] = useState<ProductCategories>();
  const [dateOfAppSignature, setDateOfAppSignature] = useState<any>(undefined);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(0);
  const { requestsStatus } = useContext(RequestStatusState);
  const { configList } = useConfigList({});
  const { calculationResult } = useContext(TableResponseData);
  const { productConfigEntry, setProductConfigEntry } = useContext(ProductConfigContext);
  const [isInvalidDayOfSignature, setisInvalidDayOfSignature] = useState({
    isInvalid: false,
    isInvalidMessage: ""
  });

  const onBlur = (date: any) => {
    setIsDatePickerBlurred(true);
    const parsedDate = dateFnsParse(dateOfAppSignature, mainDateValueFormat, new Date(date));
    if (!isValid(parsedDate)) {
      setDateOfAppSignature(undefined);
    }
  };

  useEffect(() => {
    if (dateOfAppSignature) {
      const parsedDate = dateFnsParse(dateOfAppSignature, mainDateValueFormat, new Date());
      if (isValid(parsedDate) && !isNotAfterToday(parsedDate)) {
        setIsDatePickerBlurred(true);
        setisInvalidDayOfSignature({ isInvalid: true, isInvalidMessage: `form.invalidDate` });
        setDateOfAppSignature(undefined);
      }
    }
  }, [dateOfAppSignature]);

  const [isDatePickerBlurred, setIsDatePickerBlurred] = useState(false);

  const findDateConfig = (product: ProductConfig[]) => {
    if (!dateOfAppSignature) {
      return;
    }
    const dateSignature = new Date(dateOfAppSignature);

    return product.find(el => {
      const dateFrom = el.dateFrom ? new Date(el.dateFrom) : null;
      const dateTo = el.dateTo ? new Date(el.dateTo) : null;

      return (
        (dateFrom === null || dateFrom <= dateSignature) &&
        (dateTo === null || dateTo >= dateSignature)
      );
    });
  };

  const formOptions = Object.values(ProductCategories).map((el: string) => ({
    value: el,
    label: t(`productCategory.${el}`)
  }));

  const isNotAfterToday = (date: Date) => isSameDay(new Date(), date) || !isFuture(date);

  const checkSignDaysLimit = (newDate: string = dateOfAppSignature) => {
    let dayOfSignatureValidState = { isInvalid: false, isInvalidMessage: "" };
    const dayOfSignature = new Date(newDate);
    const today = new Date();
    const signDayLimit = productConfigEntry ? productConfigEntry.applicationSignDateDaysLimit : 0;

    if (!newDate) {
      dayOfSignatureValidState = { isInvalid: true, isInvalidMessage: `form.invalidDate` };
    } else if (
      (selectedProduct && !productConfigEntry) ||
      differenceInDays(today, dayOfSignature) > signDayLimit
    ) {
      dayOfSignatureValidState = {
        isInvalid: true,
        isInvalidMessage: `form.calculationImpossibleMsg`
      };
    }
    setisInvalidDayOfSignature(dayOfSignatureValidState);
  };

  const updateDateOfSignature = (date: string) => {
    checkSignDaysLimit(date);
    setDateOfAppSignature(date);
  };

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }
    setProductConfigEntry(configList.find(el => el.productCategory === selectedProduct));
  }, [selectedProduct, configList, setProductConfigEntry]);

  useEffect(() => {
    const updateScrollbarWidth = (event: MessageEvent<any>) =>
      setScrollbarWidth(
        typeof event.data === "number" && event.source !== event.target
          ? event.data - window.innerWidth
          : 0
      );
    window.addEventListener("message", updateScrollbarWidth, { once: true });

    return () => window.removeEventListener("message", updateScrollbarWidth);
  }, []);

  useEffect(() => {
    checkSignDaysLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productConfigEntry, dateOfAppSignature]);

  return (
    <Wrapper>
      <GrayTextBox>
        <ContentRowWrapper>
          <Icon src={infoLogo} />
          <Typo16Regular>{t("message.90days", { breakingLine: <br /> })}</Typo16Regular>
        </ContentRowWrapper>
      </GrayTextBox>
      <FormContainer>
        <Form scrollbarWidth={scrollbarWidth}>
          {configList.length > 0 && (
            <>
              <MainSelect
                rowNumber={1}
                labelTrKey={`form.nameSurance`}
                options={formOptions}
                onChange={(data: { value: ProductCategories }) => setSelectedProduct(data.value)}
              />
              <Datepicker
                onBlur={onBlur}
                isInvalidMessage={isInvalidDayOfSignature.isInvalidMessage}
                isInvalid={isInvalidDayOfSignature.isInvalid && isDatePickerBlurred}
                rowNumber={2}
                labelTrKey={`form.dateOfAppSignature`}
                id={`dayPick`}
                onChange={(date: any) => updateDateOfSignature(date)}
                value={dateOfAppSignature}
                placeholder="common.datepicker.placeholder"
              />
            </>
          )}
          {selectedProduct &&
            dateOfAppSignature &&
            productConfigEntry &&
            !isInvalidDayOfSignature.isInvalid && (
              <>
                <ProductForm
                  findDateConfig={findDateConfig(productConfigEntry.config)}
                  key={`${selectedProduct}_${dateOfAppSignature}`}
                  selectedProduct={selectedProduct}
                  dateOfAppSignature={dateOfAppSignature}
                />
              </>
            )}
        </Form>
        {calculationResult.length > 0 &&
          selectedProduct &&
          !requestsStatus?.getCalculationRequest.isLoading && (
            <OfferComponent
              calculationResult={calculationResult}
              selectedProduct={selectedProduct}
            />
          )}
      </FormContainer>
    </Wrapper>
  );
};

export default Home;

const FormContainer = styled.div`
  margin: 0 auto;
`;

const Form = styled.div<{ scrollbarWidth: number }>`
  max-width: 606px;
  min-height: calc(300px + 250px - 80px);
  margin: 0 auto;
  @media (max-width: ${({ scrollbarWidth }) => 959 - scrollbarWidth}px) {
    max-width: 100%;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding-bottom: 80px;
  max-width: 1200px;
  padding-top: 50px;
  margin: 0 auto;

  @page {
    size: landscape;
    margin: 1cm 0;
  }

  @media (max-width: 1200px) {
    padding: 50px 20px 80px;
  }
  @media print {
    padding-bottom: 0;
  }
`;

const ContentRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const Icon = styled.img`
  margin-right: 17px;
  filter: invert(58%) sepia(45%) saturate(10%) hue-rotate(68deg) brightness(95%) contrast(100%);
`;
